// Money reports: daily collections, revenue and payer share not yet claimed
// (docs/plans/phase1-reports.md §2.5–2.7 and §6).
//
// SQL sums in bigint cents and returns grouped facts; this file shapes them and checks
// the invariants a finance manager relies on. Nothing here uses floating point.

import type { PaymentMethod, PaymentStatus, RefundMethod } from '../billing/shift.ts';
import { PAYMENT_METHODS, REFUND_METHOD_SOURCE } from '../billing/shift.ts';
import type { Money } from '../money.ts';
import { addMoney, subtractMoney, sumMoney, zeroMoney } from '../money.ts';
import type { CalendarDay } from '../time.ts';
import { compareDays, daysBetween, eatDayOf } from '../time.ts';

// --- Collections -------------------------------------------------------------------------

export interface MethodTotals {
  readonly received: Money;
  readonly refunded: Money;
  /** received − refunded; negative when more went out than came in. */
  readonly net: Money;
}

export interface CollectionsByMethod {
  readonly byMethod: Record<PaymentMethod, MethodTotals>;
  readonly total: MethodTotals;
}

/**
 * Money in and out by method (P4, P15). Only completed payments are money; refunds count
 * against the method they came out of (an M-Pesa reversal against M-Pesa).
 */
export function collectionsByMethod(input: {
  readonly payments: readonly {
    readonly method: PaymentMethod;
    readonly status: PaymentStatus;
    readonly amount: Money;
  }[];
  readonly refundsPaid: readonly { readonly method: RefundMethod; readonly amount: Money }[];
}): CollectionsByMethod {
  const received = new Map<PaymentMethod, Money>();
  const refunded = new Map<PaymentMethod, Money>();
  for (const payment of input.payments) {
    if (payment.status !== 'completed') continue;
    received.set(
      payment.method,
      addMoney(received.get(payment.method) ?? zeroMoney(), payment.amount),
    );
  }
  for (const refund of input.refundsPaid) {
    const method = REFUND_METHOD_SOURCE[refund.method];
    refunded.set(method, addMoney(refunded.get(method) ?? zeroMoney(), refund.amount));
  }
  const byMethod = Object.fromEntries(
    PAYMENT_METHODS.map((method) => {
      const r = received.get(method) ?? zeroMoney();
      const out = refunded.get(method) ?? zeroMoney();
      return [method, { received: r, refunded: out, net: subtractMoney(r, out) }];
    }),
  ) as Record<PaymentMethod, MethodTotals>;
  const all = PAYMENT_METHODS.map((m) => byMethod[m]);
  const total = {
    received: sumMoney(all.map((t) => t.received)),
    refunded: sumMoney(all.map((t) => t.refunded)),
    net: sumMoney(all.map((t) => t.net)),
  };
  return { byMethod, total };
}

/**
 * A payment entered on a later EAT day than it was received: a paper receipt typed in
 * after the day ended (P16). Listed separately so a reprint that differs shows why.
 */
export function enteredAfterDayEnded(receivedAt: Date, enteredAt: Date): boolean {
  return compareDays(eatDayOf(enteredAt), eatDayOf(receivedAt)) > 0;
}

export type ShiftReconciliation =
  | { readonly state: 'open' }
  | { readonly state: 'reconciles' }
  | { readonly state: 'differs'; readonly methods: readonly PaymentMethod[] };

/**
 * Whether a closed shift's recorded totals by method match the completed payments now on
 * it. A difference is a data problem to look into, never hidden (plan §6, finance.ts).
 */
export function reconcileShift(
  recorded: Readonly<Record<string, { readonly received: Money }>> | null,
  actualReceived: Readonly<Partial<Record<PaymentMethod, Money>>>,
): ShiftReconciliation {
  if (recorded === null) return { state: 'open' };
  const methods = PAYMENT_METHODS.filter((method) => {
    const expected = recorded[method]?.received.cents ?? 0n;
    const actual = actualReceived[method]?.cents ?? 0n;
    return expected !== actual;
  });
  return methods.length === 0 ? { state: 'reconciles' } : { state: 'differs', methods };
}

// --- Revenue -----------------------------------------------------------------------------

/** One group of charges from SQL: same category, payer and scheme. */
export interface RevenueFact {
  readonly category: string;
  readonly payerId: string;
  readonly payerName: string;
  readonly schemeId: string | null;
  readonly schemeName: string | null;
  readonly chargeCount: number;
  /** Charges with no price yet: counted, never valued at zero. */
  readonly unpricedCount: number;
  readonly billed: Money;
  readonly payerShare: Money;
  readonly patientShare: Money;
}

export interface RevenueRow {
  readonly key: string;
  readonly chargeCount: number;
  readonly unpricedCount: number;
  readonly billed: Money;
  readonly payerShare: Money;
  readonly patientShare: Money;
}

function addRow(a: RevenueRow | undefined, key: string, fact: RevenueFact): RevenueRow {
  if (a === undefined)
    return {
      key,
      chargeCount: fact.chargeCount,
      unpricedCount: fact.unpricedCount,
      billed: fact.billed,
      payerShare: fact.payerShare,
      patientShare: fact.patientShare,
    };
  return {
    key,
    chargeCount: a.chargeCount + fact.chargeCount,
    unpricedCount: a.unpricedCount + fact.unpricedCount,
    billed: addMoney(a.billed, fact.billed),
    payerShare: addMoney(a.payerShare, fact.payerShare),
    patientShare: addMoney(a.patientShare, fact.patientShare),
  };
}

/**
 * Groups revenue facts by category ("department" until departments exist, P2) or by payer
 * and scheme. Throws when a group's shares don't add up to what was billed: SQL summed
 * rows the database already checks, so a mismatch is a bug, not a report line.
 */
export function groupRevenue(
  facts: readonly RevenueFact[],
  by: 'category' | 'payer',
): RevenueRow[] {
  const rows = new Map<string, RevenueRow>();
  for (const fact of facts) {
    if (fact.payerShare.cents + fact.patientShare.cents !== fact.billed.cents)
      throw new RangeError('Payer and patient shares do not add up to the amount billed');
    const key = by === 'category' ? fact.category : `${fact.payerId}|${fact.schemeId ?? ''}`;
    rows.set(key, addRow(rows.get(key), key, fact));
  }
  return [...rows.values()].sort(
    (a, b) =>
      (b.billed.cents > a.billed.cents ? 1 : b.billed.cents < a.billed.cents ? -1 : 0) ||
      a.key.localeCompare(b.key),
  );
}

export interface RevenueNet {
  readonly billed: Money;
  readonly waivers: Money;
  readonly discounts: Money;
  readonly creditNotes: Money;
  /** billed − waivers − discounts − credit notes. */
  readonly net: Money;
}

/** Waivers and discounts are reported separately, then taken off (billing question 8). */
export function revenueNet(input: {
  readonly billed: Money;
  readonly waivers: Money;
  readonly discounts: Money;
  readonly creditNotes: Money;
}): RevenueNet {
  return {
    ...input,
    net: subtractMoney(
      subtractMoney(subtractMoney(input.billed, input.waivers), input.discounts),
      input.creditNotes,
    ),
  };
}

// --- Payer share not yet claimed (P14) -----------------------------------------------------

export const AGEING_BUCKETS = ['days_0_30', 'days_31_60', 'days_61_90', 'days_over_90'] as const;
export type AgeingBucket = (typeof AGEING_BUCKETS)[number];

/** The usual 30-day ageing buckets, by EAT days since the charge's event day. */
export function ageingBucket(eventDay: CalendarDay, asAt: CalendarDay): AgeingBucket {
  const age = Math.max(0, daysBetween(eventDay, asAt));
  if (age <= 30) return 'days_0_30';
  if (age <= 60) return 'days_31_60';
  if (age <= 90) return 'days_61_90';
  return 'days_over_90';
}

export interface UnclaimedFact {
  readonly payerId: string;
  readonly payerName: string;
  readonly eventDay: CalendarDay;
  readonly chargeCount: number;
  readonly visitCount: number;
  readonly payerShare: Money;
}

export interface UnclaimedRow {
  readonly payerId: string;
  readonly payerName: string;
  readonly chargeCount: number;
  readonly byBucket: Record<AgeingBucket, Money>;
  readonly total: Money;
}

function emptyBuckets(): Record<AgeingBucket, Money> {
  return {
    days_0_30: zeroMoney(),
    days_31_60: zeroMoney(),
    days_61_90: zeroMoney(),
    days_over_90: zeroMoney(),
  };
}

/** Payer share not yet claimed, by payer and age, largest first. */
export function groupUnclaimed(facts: readonly UnclaimedFact[], asAt: CalendarDay): UnclaimedRow[] {
  const rows = new Map<string, UnclaimedRow>();
  for (const fact of facts) {
    const bucket = ageingBucket(fact.eventDay, asAt);
    const current = rows.get(fact.payerId) ?? {
      payerId: fact.payerId,
      payerName: fact.payerName,
      chargeCount: 0,
      byBucket: emptyBuckets(),
      total: zeroMoney(),
    };
    rows.set(fact.payerId, {
      ...current,
      chargeCount: current.chargeCount + fact.chargeCount,
      byBucket: {
        ...current.byBucket,
        [bucket]: addMoney(current.byBucket[bucket], fact.payerShare),
      },
      total: addMoney(current.total, fact.payerShare),
    });
  }
  return [...rows.values()].sort(
    (a, b) =>
      (b.total.cents > a.total.cents ? 1 : b.total.cents < a.total.cents ? -1 : 0) ||
      a.payerName.localeCompare(b.payerName),
  );
}
